import { ArrowBack } from "@mui/icons-material";
import { Alert, Box, Button, Grid, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { type OrganizationRecord, useAllOrganizations } from "@/hooks/use-all-organizations";
import { useSaveDeliveryOption } from "@/hooks/use-delivery-option";
import { useDeliveryOptionForm } from "@/hooks/use-delivery-option-form";
import { buildAssignments, errMsg } from "@/utils/delivery-options";
import { BasicInfoSection } from "../components/Delivery/delivery-options/components/BasicInfoSection";
import { BehaviorSection } from "../components/Delivery/delivery-options/components/BehaviorSection";
import { VisibilitySection } from "../components/Delivery/delivery-options/components/VisibilitySection";
import { defaultValues } from "../schemas/DeliveryForm/delivery-option.schema";

interface FormProps {
  orgs: OrganizationRecord[];
}

function CreateDeliveryOptionForm({ orgs }: FormProps) {
  const navigate = useNavigate();
  const { saveOption, isPending } = useSaveDeliveryOption();

  const [error, setError] = useState<string | null>(null);
  const form = useDeliveryOptionForm({
    defaultValues: {
      ...defaultValues,
      partnerAssignments: buildAssignments(orgs, null, defaultValues.name),
    },
    onSubmit: async (value) => {
      setError(null);
      try {
        await saveOption({ isEdit: false, optionId: undefined, value });
        navigate("/admin/options");
      } catch (err) {
        setError(errMsg(err));
      }
    },
  });

  return (
    <Box sx={{ px: 3, py: 3 }}>
      <Box sx={{ bgcolor: "background.default", px: 2, py: 2, mb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title="Back">
              <IconButton
                size="small"
                onClick={() => navigate("/admin/options")}
                disabled={isPending}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                New option
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate("/admin/options")}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="small"
              loading={isPending}
              form="create-option-form"
            >
              Create
            </Button>
          </Stack>
        </Stack>
      </Box>

      <form.Subscribe
        selector={(state) => ({
          submitError: state.errorMap.onSubmit,
          attempts: state.submissionAttempts,
          isValid: state.isValid,
        })}
      >
        {({ submitError, attempts, isValid }) => {
          if (attempts === 0 || isValid || !submitError) return null;
          const message =
            typeof submitError === "string"
              ? submitError
              : "Le formulaire contient des erreurs. Vérifiez les champs en surbrillance.";
          return (
            <Alert severity="error" sx={{ mb: 2 }}>
              {message}
            </Alert>
          );
        }}
      </form.Subscribe>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form
        id="create-option-form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.AppForm>
          <Grid container spacing={2} mt={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <BasicInfoSection form={form} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <BehaviorSection form={form} />
            </Grid>
            <Grid size={12}>
              <VisibilitySection form={form} />
            </Grid>
          </Grid>
        </form.AppForm>
      </form>
    </Box>
  );
}

function CreateDeliveryOption() {
  const { organizations: orgs, isLoading } = useAllOrganizations();

  if (isLoading) {
    return <Box sx={{ p: 3 }}>Loading...</Box>;
  }

  return <CreateDeliveryOptionForm orgs={orgs} />;
}

export default CreateDeliveryOption;
